import { BrowserModule } from '@angular/platform-browser';
import { NgModule, ErrorHandler, Injectable } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { RouterModule } from '@angular/router';
import { Router } from '@angular/router';
import { MsalModule, MsalService, MsalGuard, MsalInterceptor, MsalRedirectComponent } from '@azure/msal-angular';
import { PublicClientApplication, InteractionType } from '@azure/msal-browser';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { ErrorService } from 'app/_services/error.service';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { DatePipe } from '@angular/common';
import * as StackTrace from 'stacktrace-js';
import { CookieService } from 'ngx-cookie-service';
import { CommunicationService } from './_services/communication.service';
import { ITranslationsClass } from './_models/ITranslationsClass';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

import { AppComponent } from './app.component';
import { LoginComponent } from './login-component/login.component';
import { GridComponent } from './grid-component/grid.component';
import { MenuComponent } from './menu-component/menu.component';
import { DetailGridComponent } from './detail-grid-component/detail-grid.component';
import { Direct } from './_misc/direct';
import { FormActionComponent } from './form-action-component/form-action.component';
import { ColumnComponent } from './column-component/column.component';
import { FeedbackComponent } from './feedback-component/feedback.component';
import { ErrorComponent } from './error-component/error.component';
import { SettingsComponent } from './settings-component/settings.component';
import { NotificationComponent } from './notification-component/notification.component';
import { DashboardComponent } from './dashboard-component/dashboard.component';
import { GridActionComponent } from './grid-action-component/grid-action.component';
import { ImageViewerComponent } from './image-viewer-component/image-viewer.component';
import { ReportComponent } from './report-component/report.component';
import { UserMenuComponent } from './user-menu-component/user-menu.component';
import { RadComponent } from './rad-component/rad.component';
import { BaseComponent } from './base-component/base.component';
import { GanttComponent } from './gantt-component/gantt.component';
import { SqlActionComponent } from './sql-action-component/sql-action.component';
import { ImportComponent } from './import-component/import.component';
import { MapComponent } from './map-component/map.component';
import { AdvancedFiltersComponent } from './advanced-filters-component/advanced-filters.component';
import { ExportComponent } from './export-component/export.component';
import { CustomDatePickerComponent } from './custom-date-picker-component/custom-date-picker.component';
import { DialogHeaderComponent } from './dialog-header-component/dialog-header.component';
import { StorageManagementComponent } from './storage-management-component/storage-management.component';
import { DashboardCarouselComponent } from './dashboard-carousel-component/dashboard-carousel.component';
import { WidgetComponent } from './widget-component/widget.component';
import { ChartComponent } from './chart-component/chart.component';
import { WidgetExpandedComponent } from './widget-expanded-component/widget-expanded.component';

import { DxReportViewerModule, DxReportDesignerModule } from 'devexpress-reporting-angular';
import { DxDashboardControlModule } from 'devexpress-dashboard-angular';
import { DxGanttModule } from 'devextreme-angular';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ClipboardModule } from 'ngx-clipboard';
import { AccordionModule } from 'primeng/accordion';
import { SliderModule } from 'primeng/slider';
import { PasswordModule } from 'primeng/password';
import { DropdownModule } from 'primeng/dropdown';
import { ScrollTopModule } from 'primeng/scrolltop';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { NgChartsModule } from 'ng2-charts';
import { CalendarModule } from 'primeng/calendar';
import { TabViewModule } from 'primeng/tabview';
import { GoogleMapsModule } from '@angular/google-maps';
import { NgxAngularQueryBuilderModule } from "ngx-angular-query-builder";
import { MatSliderModule } from '@angular/material/slider';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { msConfig } from '../app/ms-config';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
//#region Error Handler
@Injectable({ providedIn: 'root' })
export class GlobalErrorHandler implements ErrorHandler {
  constructor(
    private router: Router,
    private errorService: ErrorService,
    private cookieService: CookieService,
    private communicationService: CommunicationService
  ) { }

  handleError(error: any) {
    console.log(error.message);
    console.log(error);
    //return;
    if (error.message == 'object unsubscribed') return;
    StackTrace.fromError(error).then((stackframes) => {
      console.log(stackframes);
      let stackString = '';
      for (let sf of stackframes) {
        let filename = sf.getFileName()?.toLowerCase();
        let functionName = sf.getFunctionName()?.toLowerCase();

        if (filename?.includes('component') ||
          filename?.includes('service') ||
          filename?.includes('controller') ||
          functionName?.includes('component') ||
          functionName?.includes('service') ||
          functionName?.includes('controller')) {
          stackString += `Function: ${sf.getFunctionName() || 'anonymous'}<br>`;
          stackString += `File: ${sf.getFileName().replace('webpack:///src/app/', '')}<br>`;
          stackString += `Line: ${sf.getLineNumber()}:${sf.getColumnNumber()}<br><br>`;
        }
      }
      console.log(stackString);

      let nodeId = -1;
      if (sessionStorage.getItem('nodeId')) {
        nodeId = Number(sessionStorage.getItem('nodeId')!.toString());
      }
      console.log(nodeId);

      let userId = localStorage.getItem('qx_userId') ? Number(localStorage.getItem('qx_userId')) : -1;
      console.log(userId);

      if (error.message.toString().includes('_')) return;
      return;
      if (userId > -1) {
        let formData = new FormData();
        formData.append('nodeId', encodeURIComponent(nodeId));
        formData.append('userId', encodeURIComponent(userId));
        formData.append('stackString', encodeURIComponent(stackString));
        formData.append('error', encodeURIComponent(error.message.toString()));
        formData.append('host', encodeURIComponent(window.location.hostname));
        this.errorService.logError(formData).subscribe({
          next: (data: any) => {
            console.log(data);

            if (data.refNumber == 'NOINFO') return;

            let detailedErrorsEnabled = false;
            if (this.cookieService.check('detailedErrors') && this.cookieService.get('detailedErrors') == 'true')
              detailedErrorsEnabled = true;

            if (detailedErrorsEnabled) {
              sessionStorage.setItem('errorDetails', JSON.stringify(data));
              this.router.navigate(["error"]);
              return;
            }

            this.communicationService.showErrorAlert(data.refNumber);
          }
        });
      }
    });
  }
}
//#endregion

export function MSALInstanceFactory() {
  if (!localStorage.getItem('qx_msconfig')) {
    fetch(
    '/util/getmsdetails', { method: 'GET' })
    .then(response => response.json())
    .then(config => {      
      localStorage.setItem('qx_msconfig', JSON.stringify(config));
      msConfig.client = config.client;
      msConfig.authority = config.authority;
      msConfig.redirecturi = config.redirecturi;      
      return platformBrowserDynamic().bootstrapModule(AppModule);
    })
  } else {
    let config = JSON.parse(localStorage.getItem('qx_msconfig'));
    msConfig.client = config.client;
    msConfig.authority = config.authority;
    msConfig.redirecturi = config.redirecturi;
  }  
  
  return new PublicClientApplication({
    auth: {
      clientId: msConfig.client,
      authority: msConfig.authority, 
      redirectUri: (msConfig.redirecturi == 'default' || !msConfig.redirecturi) ? window.location.origin : msConfig.redirecturi
    },
    cache: {
      cacheLocation: 'localStorage'
    }
  });
}

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    GridComponent,
    MenuComponent,
    DetailGridComponent,
    FormActionComponent,
    ColumnComponent,
    FeedbackComponent,
    ErrorComponent,
    SettingsComponent,
    NotificationComponent,
    DashboardComponent,
    GridActionComponent,
    ImageViewerComponent,
    ReportComponent,
    UserMenuComponent,
    RadComponent,
    BaseComponent,
    GanttComponent,
    SqlActionComponent,
    ImportComponent,
    MapComponent,
    AdvancedFiltersComponent,
    ExportComponent,
    CustomDatePickerComponent,
    DialogHeaderComponent,
    StorageManagementComponent,
    DashboardCarouselComponent,
    WidgetComponent,
    ChartComponent,
    WidgetExpandedComponent
  ],
  bootstrap: [AppComponent],
  imports: [
    MsalModule.forRoot(
      MSALInstanceFactory(),
      {
        interactionType: InteractionType.Redirect,
        authRequest: {
          scopes: ['openid', 'profile', 'email']
        }
      },
      {
        interactionType: InteractionType.Redirect,
        protectedResourceMap: new Map([
          ['http://localhost:4200/', [`api://${msConfig.client}/access_as_user`]]
        ])
      }
    ),
    FontAwesomeModule,
    BrowserModule.withServerTransition({ appId: 'ng-cli-universal' }),
    FormsModule,
    DxReportViewerModule,
    DxReportDesignerModule,
    DxDashboardControlModule,
    DxGanttModule,
    TableModule,
    ButtonModule,
    DialogModule,
    TooltipModule,
    InputTextareaModule,
    CheckboxModule,
    MatTableModule,
    MatTabsModule,
    MatTooltipModule,
    MatCheckboxModule,
    MatPaginatorModule,
    MatSlideToggleModule,
    ClipboardModule,
    AccordionModule,
    SliderModule,
    PasswordModule,
    DropdownModule,
    ScrollTopModule,
    TabViewModule,
    CalendarModule,
    BrowserAnimationsModule,
    NgChartsModule,
    NgbModule,
    OverlayPanelModule,
    GoogleMapsModule,
    NgxAngularQueryBuilderModule,
    MatSliderModule,
    DragDropModule,
    RouterModule.forRoot([
      { path: '', component: BaseComponent, pathMatch: 'full' },
      { path: 'base', component: BaseComponent },
      { path: 'grid', component: BaseComponent },
      { path: 'gantt', component: BaseComponent },
      { path: 'signin', component: LoginComponent },
      { path: 'direct', component: Direct },
      { path: 'error', component: ErrorComponent },
      { path: 'report-designer', component: ReportComponent },
      { path: 'report-viewer', component: ReportComponent },
      { path: 'dashboard-designer', component: DashboardComponent },
      { path: 'dashboard-viewer', component: DashboardComponent },
      { path: 'dashboard-carousel', component: DashboardCarouselComponent },
      { path: '**', redirectTo: 'base', component: BaseComponent },
    ])], providers: [
      AppComponent,
      GridComponent,
      MenuComponent,
      DetailGridComponent,
      ColumnComponent,
      FormActionComponent,
      FeedbackComponent,
      SqlActionComponent,
      Direct,
      SettingsComponent,
      LoginComponent,
      NotificationComponent,
      DashboardComponent,
      GridActionComponent,
      ImageViewerComponent,
      DatePipe,
      ReportComponent,
      UserMenuComponent,
      RadComponent,
      BaseComponent,
      GanttComponent,
      ImportComponent,
      MapComponent,
      AdvancedFiltersComponent,
      ExportComponent,
      ErrorComponent,
      {
        provide: ErrorHandler,
        useClass: GlobalErrorHandler
      },
      CustomDatePickerComponent,
      DialogHeaderComponent,
      ITranslationsClass,
      StorageManagementComponent,
      DashboardCarouselComponent,
      WidgetComponent,
      ChartComponent,
      WidgetExpandedComponent,
      provideHttpClient(withInterceptorsFromDi()),
      { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
      MsalGuard
    ]
})
export class AppModule { }
